// src/pages/chat/api.js
import axios from "axios";
import Cookies from "js-cookie";
import {
  emitSubscriptionRequired,
  isSubscriptionRequiredError,
} from "../../events/subscriptionEvents";
import { emitPlanLimit, isPlanLimitError, planLimitMessage } from "../../events/planLimitEvents";
import { clearCachedSubscriptionStatus } from "../../utils/subscriptionStatus";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const getFreshAccessToken = async () => {
  let token = Cookies.get("access_token");
  const refreshToken = Cookies.get("refresh_token");

  if (!token || !refreshToken) {
    logoutUser();
    return null;
  }

  // Decode JWT token to check expiry
  try {
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));
    const now = Math.floor(Date.now() / 1000);

    if (payload.exp < now) {
      // token expired, try refreshing
      const refreshResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}api/token/refresh/`,
        { refresh: refreshToken }
      );
      token = refreshResponse.data.access;
      Cookies.set("access_token", token);
      localStorage.setItem("access_token", token);
    }

    return token;
  } catch (err) {
    logoutUser();
    return null;
  }
};

const logoutUser = () => {
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  clearCachedSubscriptionStatus();
  localStorage.clear();
  window.location.href = "/signin";
};

// Decode the current user's claims (user_id, email) from the access token.
export const getCurrentUser = () => {
  try {
    const token = Cookies.get("access_token");
    if (!token) return null;
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));
    return { user_id: payload.user_id, email: payload.email };
  } catch {
    return null;
  }
};

// Add access token automatically
api.interceptors.request.use((config) => {
  const token = Cookies.get("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshWaiters = [];

const notifyWaiters = (newToken) => {
  refreshWaiters.forEach((cb) => cb(newToken));
  refreshWaiters = [];
};

const refreshAccessToken = async () => {
  const refreshToken = Cookies.get("refresh_token");
  if (!refreshToken) throw new Error("no-refresh-token");

  const { data } = await axios.post(
    `${process.env.REACT_APP_API_URL}api/token/refresh/`,
    { refresh: refreshToken }
  );

  const newAccess = data.access;
  Cookies.set("access_token", newAccess);
  localStorage.setItem("access_token", newAccess);
  return newAccess;
};

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isSubscriptionRequiredError(error)) {
      emitSubscriptionRequired();
      return Promise.reject(error);
    }

    if (isPlanLimitError(error)) emitPlanLimit(planLimitMessage(error), error.response.data.limit_kind);

    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    // Expired access token comes back as 401 from standard DRF views, but as
    // 403 from custom JWTProtectedAPIView (authentication_classes = []), which
    // makes DRF downgrade 401→403. Match on status, not the body `code`, which
    // the 403 path can omit. For 403 we still require a JWT-error shape so we
    // don't refresh on genuine permission-denied responses.
    const isTokenError =
      status === 401 ||
      (status === 403 &&
        (data?.code === "token_not_valid" ||
          Array.isArray(data?.messages) ||
          (typeof data?.detail === "string" && /token/i.test(data.detail))));

    if (isTokenError && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshWaiters.push((newToken) => {
            if (!newToken) return reject(error);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        notifyWaiters(newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        isRefreshing = false;
        notifyWaiters(null);
        logoutUser();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
